import React, { useMemo } from 'react';
import { DatePicker } from 'antd';
import { SearchOutlined, UndoOutlined, SafetyCertificateOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { useReports } from '../context/ReportsContext';

const { RangePicker } = DatePicker;

export default function ReportsFilterToolbar({ employees = [], projects = [] }) {
    const { filters, updateFilter, resetFilters, triggerRefresh } = useReports();
    const currentAdmin = useSelector(selectCurrentAdmin);

    const userRole = (currentAdmin?.role || currentAdmin?.adminRole || '').toLowerCase();
    const isElevated = ['super_admin', 'admin', 'director', 'general manager'].includes(userRole);
    const currentAdminId = String(currentAdmin?._id || currentAdmin?.id || '');

    // Resolve accessible projects for this user
    const accessibleProjects = useMemo(() => {
        if (isElevated) return projects || [];
        return (projects || []).filter(p => {
            const pmId = String(p.projectManager?._id || p.projectManager || '');
            const ownerId = String(p.createdBy?._id || p.createdBy || p.owner?._id || p.owner || '');
            return pmId === currentAdminId || ownerId === currentAdminId;
        });
    }, [projects, isElevated, currentAdminId]);

    const isNonPmEmployee = !isElevated && accessibleProjects.length === 0;

    const periodOptions = [
        { label: 'This Week', value: 'this_week' },
        { label: 'Today', value: 'today' },
        { label: 'This Month', value: 'this_month' },
        { label: 'This Quarter', value: 'this_quarter' },
        { label: 'Custom Duration', value: 'custom' },
    ];

    // Dynamic Department Options: Scoped to managed projects if employee
    const departmentOptions = useMemo(() => {
        if (isElevated) {
            return [
                { label: 'All Departments', value: 'all' },
                { label: 'Development', value: 'Development' },
                { label: 'Design & Creative', value: 'Design' },
                { label: 'Marketing', value: 'Marketing' },
                { label: 'QA / Testing', value: 'QA' },
                { label: 'Operations', value: 'Operations' },
            ];
        }
        const deptSet = new Set(accessibleProjects.map(p => p.department).filter(Boolean));
        if (deptSet.size === 0) {
            return [{ label: 'My Department', value: currentAdmin?.department || 'all' }];
        }
        return [
            { label: 'All Managed Departments', value: 'all' },
            ...Array.from(deptSet).map(d => ({ label: d, value: d })),
        ];
    }, [isElevated, accessibleProjects, currentAdmin?.department]);

    // Dynamic Team Member Options: Scoped to members on managed projects if employee
    const teamMemberOptions = useMemo(() => {
        if (isElevated) return employees || [];
        const allowedMemberIds = new Set();
        accessibleProjects.forEach(p => {
            (p.members || []).forEach(m => allowedMemberIds.add(String(m?._id || m)));
            (p.collaborators || []).forEach(c => allowedMemberIds.add(String(c?._id || c)));
            if (p.projectManager) allowedMemberIds.add(String(p.projectManager?._id || p.projectManager));
            if (p.createdBy) allowedMemberIds.add(String(p.createdBy?._id || p.createdBy));
        });
        allowedMemberIds.add(currentAdminId);
        return (employees || []).filter(e => allowedMemberIds.has(String(e._id || e.id)));
    }, [isElevated, accessibleProjects, employees, currentAdminId]);

    const projectTypeOptions = [
        { label: 'All Projects', value: 'all' },
        { label: 'Client Work', value: 'Client' },
        { label: 'In-house Work', value: 'In-house' },
    ];

    const handleRangeChange = (dates, dateStrings) => {
        if (dates && dates[0] && dates[1]) {
            updateFilter('startDate', dateStrings[0]);
            updateFilter('endDate', dateStrings[1]);
        } else {
            updateFilter('startDate', null);
            updateFilter('endDate', null);
        }
    };

    const vanillaSelectClass = 'bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 text-sm font-semibold text-[#191c1d] dark:text-white rounded-xl focus:ring-2 focus:ring-[#00AAF1] py-2.5 px-3.5 shadow-sm cursor-pointer outline-none';

    return (
        <div className="space-y-3 mb-6">
            {/* Scoping notice for employees who are PMs or Project Owners */}
            {!isElevated && accessibleProjects.length > 0 && (
                <div className="p-3 bg-blue-50/80 dark:bg-neutral-950 border border-blue-200/60 dark:border-neutral-800 rounded-xl text-xs text-blue-900 dark:text-blue-300 flex items-center justify-between">
                    <span className="flex items-center gap-2 font-medium">
                        <SafetyCertificateOutlined className="text-blue-500 text-sm" />
                        <span><strong>Project Manager Scope Active:</strong> Filter options and reports are scoped strictly to the <strong>{accessibleProjects.length} project(s)</strong> you manage or own.</span>
                    </span>
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                        {teamMemberOptions.length} team members accessible
                    </span>
                </div>
            )}

            {/* Scoping notice if user has no managed projects */}
            {isNonPmEmployee && (
                <div className="p-3.5 bg-amber-50/80 dark:bg-neutral-950 border border-amber-200/70 dark:border-neutral-800 rounded-xl text-xs text-amber-900 dark:text-amber-300 flex items-start gap-2.5">
                    <InfoCircleOutlined className="text-amber-500 text-sm mt-0.5 shrink-0" />
                    <span>
                        <strong>Standard Contributor View:</strong> You are not currently designated as a Project Manager or Project Owner on any active project. Departmental portfolio reports and forecasts are reserved for Project Managers, Owners, and Executive Leadership.
                    </span>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#f3f4f5] dark:bg-black p-2.5 sm:p-3 rounded-2xl max-w-full border border-slate-200/60 dark:border-neutral-800 shadow-xs">
                <div className="flex flex-wrap items-center gap-3">
                    {/* Reporting Period */}
                    <div className="relative shrink-0">
                        <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-black text-xs font-bold text-[#424750] dark:text-slate-400 px-1.5 rounded z-10 pointer-events-none">
                            Period
                        </label>
                        <select
                            value={filters.period}
                            onChange={(e) => updateFilter('period', e.target.value)}
                            className={vanillaSelectClass}
                        >
                            {periodOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Custom Duration Date Picker */}
                    {filters.period === 'custom' && (
                        <div className="relative shrink-0">
                            <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-black text-xs font-bold text-[#424750] dark:text-slate-400 px-1.5 rounded z-10 pointer-events-none">
                                Date Range
                            </label>
                            <RangePicker
                                onChange={handleRangeChange}
                                className="bg-white dark:bg-black border-slate-200/80 dark:border-neutral-800 rounded-xl py-2 shadow-xs"
                                style={{ height: 42 }}
                            />
                        </div>
                    )}

                    {/* Department (dynamically scoped) */}
                    <div className="relative shrink-0">
                        <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-black text-xs font-bold text-[#424750] dark:text-slate-400 px-1.5 rounded z-10 pointer-events-none">
                            Department
                        </label>
                        <select
                            value={filters.department}
                            onChange={(e) => updateFilter('department', e.target.value)}
                            className={vanillaSelectClass}
                        >
                            {departmentOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Team Member (dynamically scoped) */}
                    <div className="relative shrink-0">
                        <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-black text-xs font-bold text-[#424750] dark:text-slate-400 px-1.5 rounded z-10 pointer-events-none">
                            Team Member
                        </label>
                        <select
                            value={filters.teamMember}
                            onChange={(e) => updateFilter('teamMember', e.target.value)}
                            className={vanillaSelectClass}
                            style={{ minWidth: 170 }}
                        >
                            <option value="all">
                                {isElevated ? 'All Team Members' : 'All Project Contributors'}
                            </option>
                            {teamMemberOptions.map((e) => (
                                <option key={e._id || e.id} value={e._id || e.id}>
                                    {[e.name, e.surname].filter(Boolean).join(' ') || e.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Project Scope */}
                    <div className="relative shrink-0">
                        <label className="absolute -top-2 left-3 bg-[#f8f9fa] dark:bg-black text-xs font-bold text-[#424750] dark:text-slate-400 px-1.5 rounded z-10 pointer-events-none">
                            Project Scope
                        </label>
                        <select
                            value={filters.projectType}
                            onChange={(e) => updateFilter('projectType', e.target.value)}
                            className={vanillaSelectClass}
                        >
                            {projectTypeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        type="button"
                        onClick={triggerRefresh}
                        className="px-5 py-2.5 rounded-xl text-sm font-bold bg-[#003263] hover:bg-[#00264d] dark:bg-blue-600 dark:hover:bg-blue-700 text-white shadow-xs transition-all flex items-center gap-2 cursor-pointer"
                    >
                        <SearchOutlined style={{ fontSize: 14 }} />
                        <span>Generate Report</span>
                    </button>
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="px-4 py-2.5 rounded-xl text-sm font-bold border border-slate-200 dark:border-neutral-800 bg-white dark:bg-black text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-neutral-900 transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
                        title="Reset all filters"
                    >
                        <UndoOutlined style={{ fontSize: 14 }} />
                        <span>Reset</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
